import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Download } from "lucide-react";
import { toast } from "sonner";

export default function ReportCards() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canGen = hasRole("coordinateur", "direction", "administration", "professeur");
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", term: "Trimestre 1", appreciation: "" });

  const load = async () => {
    const { data } = await api.get("/report-cards"); setRows(data);
    if (canGen) { const s = await api.get("/students"); setStudents(s.data); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    try { await api.post("/report-cards/generate", form); toast.success(t("save")); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const pdf = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/report-cards/${id}/pdf`, "_blank");

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("reportCards")}</h1>
        {canGen && <Button data-testid="rc-generate-btn" onClick={() => setOpen(true)} className="rounded-full gap-2"><Sparkles className="h-4 w-4" />{t("generateBulletin")}</Button>}
      </div>
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("student")}</TableHead><TableHead>{t("term")}</TableHead>
            <TableHead className="text-right">{t("generalAvg")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-10 text-slate-400">{t("noData")}</TableCell></TableRow> :
              rows.map((r, i) => (
                <TableRow key={r.id} data-testid="row-report-cards" className={i % 2 ? "bg-slate-50/40" : ""}>
                  <TableCell className="font-medium text-slate-800">{r.student_name}</TableCell>
                  <TableCell className="text-slate-600">{r.term}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-primary">{r.overall_average}/20</TableCell>
                  <TableCell className="text-right"><Button data-testid="rc-pdf-btn" variant="ghost" size="icon" onClick={() => pdf(r.id)}><Download className="h-4 w-4 text-primary" /></Button></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{t("generateBulletin")}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("student")}</label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="rc-student"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("term")}</label><Input value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} /></div>
            <div className="grid gap-1.5"><label className="text-sm font-medium text-slate-600">{t("appreciation")}</label><Textarea value={form.appreciation} onChange={(e) => setForm({ ...form, appreciation: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button data-testid="rc-save-btn" onClick={generate}>{t("generate")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
